import type { ItemControl } from './ItemControls';

export type GrabRole = 'held' | 'holding';

export interface GrabRelation {
  role: GrabRole;
  rivalName: string;
}

export interface TraversalPromptProjection {
  visible: boolean;
  mode: 'hidden' | 'climbing' | GrabRole;
  eyebrow: string;
  title: string;
  detail: string;
}

/**
 * Projects mutually exclusive traversal/combat constraints into one compact
 * center-screen instruction. Grab relations come from authoritative snapshots,
 * so the prompt stays present for the entire hold instead of relying on a
 * short event toast that can be displaced by simultaneous combat feedback.
 */
export function projectTraversalPrompt(
  climbing: boolean,
  grabRelation: GrabRelation | null,
  repulsorControl?: ItemControl,
): TraversalPromptProjection {
  if (grabRelation?.role === 'held') {
    return {
      visible: true,
      mode: 'held',
      eyebrow: `HELD BY ${displayRival(grabRelation.rivalName)}`,
      title: 'RMB + M1 COUNTERATTACK',
      detail: repulsorControl
        ? `A HIT BREAKS THEIR GRIP · ${repulsorControl} REPULSOR ESCAPES`
        : 'A HIT BREAKS THEIR GRIP · AIM AT YOUR CAPTOR',
    };
  }
  if (grabRelation?.role === 'holding') {
    return {
      visible: true,
      mode: 'holding',
      eyebrow: `${displayRival(grabRelation.rivalName)} SECURED`,
      title: 'M1 THROW · AIM TOWARD DANGER',
      detail: 'THEY CAN COUNTERATTACK · MOVE NOW',
    };
  }
  if (climbing) {
    return {
      visible: true,
      mode: 'climbing',
      eyebrow: 'M1 WORLD GRAB',
      title: 'W/S CLIMB · A/D SHIMMY',
      detail: 'RELEASE M1 DROPS · SPACE KICKS CLEAR',
    };
  }
  return {
    visible: false,
    mode: 'hidden',
    eyebrow: 'M1 WORLD GRAB',
    title: 'W/S CLIMB · A/D SHIMMY',
    detail: 'RELEASE M1 DROPS · SPACE KICKS CLEAR',
  };
}

function displayRival(name: string): string {
  const normalized = name.trim().toUpperCase();
  return normalized || 'RIVAL';
}
